import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import {
  GetStyleTemplateRequest, ListStyleTemplatesRequest, ListStyleTemplatesResponse,
  SituViewWebApiClient, StyleConfigTemplate, ProductName, GetStyleTemplateResponse
} from 'situview-webapi-client';
import { AuthService, ConfirmDialogService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'style-config',
  templateUrl: './style-config.component.html',
  styleUrls: ['./style-config.component.scss']
})
export class StyleConfigComponent extends SituBaseComponent implements OnChanges {
  @Input() incomingStyleConfigTemplateId: string | undefined;
  @Input() incomingHomeId: string | undefined;
  @Input() incomingTemplateName: string | undefined;
  @Input() product: ProductName | undefined;

  @Output() onStylesChange = new EventEmitter<string[]>();
  @Output() onStyleTemplateChange = new EventEmitter<string>();
  @Output() onSavePendingChange = new EventEmitter<boolean>();

  @ViewChild('templatesContainer', { static: true }) templatesContainer: ElementRef | undefined;

  loading = false;
  styleTemplates: StyleConfigTemplate[] = [];
  filteredStyleTemplates: StyleConfigTemplate[] = [];
  selectedStyleTemplate: GetStyleTemplateResponse | undefined;

  searchField = new FormControl();

  constructor(
    authServive: AuthService,
    stateService: StateService,
    private situViewClient: SituViewWebApiClient,
    private snackBar: MatSnackBar,
    private dialogService: ConfirmDialogService) {
      super(authServive, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadStyleTemplates();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initStylesForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes?.incomingStyleConfigTemplateId?.currentValue)) {
      if (changes.incomingStyleConfigTemplateId?.currentValue !== changes.incomingStyleConfigTemplateId?.previousValue) {
        this.loadStyleTemplate(changes.incomingStyleConfigTemplateId.currentValue)?.then(() => {
          this.onStyleTemplateChange.emit(changes.incomingStyleConfigTemplateId.currentValue);
          let styleTemplateOption = this.styleTemplates.find(x => x.id === changes.incomingStyleConfigTemplateId.currentValue);
          if (styleTemplateOption) this.selectTemplate(styleTemplateOption);
        });
      }
    }
    if ((changes?.incomingHomeId?.currentValue)) {
      if (changes.incomingHomeId?.currentValue !== changes.incomingHomeId?.previousValue) {
        this.loadStyleTemplates()
      }
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  selectTemplate(template: StyleConfigTemplate) {
    this.loadStyleTemplate(template.id)!
      .then(() => { this.onStyleTemplateChange.emit(template.id); })
  }

  clearTemplate() {
    this.selectedStyleTemplate = undefined;
    this.onStyleTemplateChange.emit(undefined);
  }

  scrollLeft() {
    this.templatesContainer?.nativeElement.scrollBy({ left: -100, behavior: 'smooth' });
  }

  scrollRight() {
    this.templatesContainer?.nativeElement.scrollBy({ left: 100, behavior: 'smooth' });
  }

  private initStylesForm() {
    this._subscriptions.push(this.searchField.valueChanges.pipe(
      startWith(''),
      debounceTime(100),
      distinctUntilChanged(),
      map(value => {
        if (typeof value != 'string') {
          this.filteredStyleTemplates = this.styleTemplates;
          return;
        } else {
          this.filteredStyleTemplates = this.styleTemplates.filter(x => x.name?.toLowerCase().includes(value?.toLowerCase()));
        }
      })
    ).subscribe());
  }

  doFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredStyleTemplates = this.styleTemplates.filter(x => x.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    } else {
      this.filteredStyleTemplates = this.styleTemplates;
    }
  }

  private loadStyleTemplates() {
    const request = new ListStyleTemplatesRequest();
    request.companyId = this.selectedCompany?.id;
    if (this.incomingHomeId) request.homeId = this.incomingHomeId;
    request.productName = this.product;
    request.isHidden = false;

    this.loading = true;
    return this.situViewClient.listStyleTemplates(request)
      .then((response: ListStyleTemplatesResponse) => {
        if (response.success) {
          this.styleTemplates = response.styleTemplates ?? [];

          const value = this.searchField?.value;
          if (typeof value != 'string') {
            this.filteredStyleTemplates = this.styleTemplates;
          }
          else {
            this.filteredStyleTemplates = this.styleTemplates.filter(x => x.name?.toLowerCase().includes(value?.toLowerCase()));
          }
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.snackBar.open(`Error loading Style Templates!`, 'OK', { duration: 2000 });
      });
  }

  getTemplateName(styleTemplate: StyleConfigTemplate) {
    return styleTemplate?.name;
  }

  private loadStyleTemplate(styleConfigTemplateId?: string) {
    if (!this.selectedStyleTemplate?.id && !styleConfigTemplateId) {
      return;
    }

    const request = new GetStyleTemplateRequest();
    request.styleTemplateId = styleConfigTemplateId ?? this.selectedStyleTemplate?.id;

    this.loading = true;
    return this.situViewClient.getStyleTemplate(request)
      .then(response => {
        if (response.success) {
          this.selectedStyleTemplate = response;
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
      });
  }
}